import React, { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { Menu, X, ImagePlus, FileText, ScanSearch, Mic2, Film, ChevronLeft, ChevronRight, FolderOpen, Image, BarChart3, Settings, Package, Receipt, UserCircle } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { GenemoFooter } from '../components/GenemoFooter'
import { useCostStore } from '../stores/costStore'
import { useVideoJobStore } from '../stores/videoJobStore'
import { useProjectStore } from '../stores/projectStore'
import { VideoJobToast } from '../components/VideoJobToast'
import { AnalysisCompleteToast } from '../components/AnalysisCompleteToast'
import { OAuthLoginToast } from '../components/OAuthLoginToast'
import { ChatWidget } from '../components/ChatWidget'
import { getUsageSummary, getUsageHistory, getRenderJobStatus, getRenderedVideos } from '../lib/api'
import type { UsageData } from '@/types/provider'
import { useTheme, type ThemeValue } from '../hooks/useTheme'
import { useBackgroundPoller } from '../hooks/useBackgroundPoller'
import { queryKeys } from '../hooks/queryKeys'
import { useSettingsQuery } from '../hooks/useSettingsQuery'
import { trackOnce } from '../../../family-design/analytics'

export const AppLayout: React.FC = () => {
  useBackgroundPoller()
  const queryClient = useQueryClient()
  const { data: appSettings } = useSettingsQuery()
  const mediaPrefetchedRef = useRef(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // テーマ管理（localStorage から初期値、APIで同期）
  const [theme, setTheme] = useState<ThemeValue>(() => {
    return (localStorage.getItem('theme') as ThemeValue) || 'light'
  })

  // 起動時にAPIからtheme設定を取得
  useEffect(() => {
    fetch('/api/settings', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.ui?.theme) {
          setTheme(data.data.ui.theme as ThemeValue)
        }
      })
      .catch(() => {})
  }, [])

  // SettingsPage からのリアルタイム変更イベント
  useEffect(() => {
    const handler = (e: Event) =>
      setTheme((e as CustomEvent<ThemeValue>).detail)
    window.addEventListener('theme-changed', handler)
    return () => window.removeEventListener('theme-changed', handler)
  }, [])

  // テーマ適用
  useTheme(theme)

  const location = useLocation()
  const navigate = useNavigate()
  const { totals, setSessionCost } = useCostStore()
  const { jobs, updateJob, removeJob, addToast, activeRenderJobs, isRenderPageMounted, addRenderToast, removeRenderJobEntry, updateRenderJobEntry } = useVideoJobStore()
  const { project: currentProject } = useProjectStore()
  const [isCostDetailOpen, setIsCostDetailOpen] = useState(false)
  const [costDetails, setCostDetails] = useState<UsageData[]>([])
  const [isCostDetailsLoading, setIsCostDetailsLoading] = useState(false)
  const [costDetailsError, setCostDetailsError] = useState<string | null>(null)
  const [costDetailPage, setCostDetailPage] = useState(0)
  const COST_PAGE_SIZE = 20
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false)
  const projectMenuRef = useRef<HTMLDivElement | null>(null)
  const costDetailRef = useRef<HTMLDivElement | null>(null)
  const projectIdMatch = location.pathname.match(/^\/project\/([^/]+)/)
  const projectId = projectIdMatch?.[1]

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  // ページ遷移でモバイルサイドバーを自動で閉じる
  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname])

  // バックグラウンドでメディアリストをプリロード（/projects 着地時、2秒遅延で1回だけ）
  useEffect(() => {
    if (mediaPrefetchedRef.current) return
    mediaPrefetchedRef.current = true
    const timer = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.media.list({
          type: 'all',
          dateRange: 'all',
          search: '',
          sortBy: 'createdAt',
          sortOrder: 'desc',
          limit: 12,
          offset: 0,
        }),
        queryFn: async () => {
          const params = new URLSearchParams({
            sortBy: 'createdAt',
            sortOrder: 'desc',
            limit: '12',
            offset: '0',
          })
          const res = await fetch(`/api/media?${params}`, { credentials: 'include' })
          const data = await res.json()
          return data.success
            ? data.data
            : { items: [], total: 0, hasMore: false, limit: 12, offset: 0 }
        },
        staleTime: 1000 * 60 * 5,
      })
    }, 2000)
    return () => clearTimeout(timer)
  }, [queryClient])

  // 動画生成ジョブのポーリング（5秒ごと）
  useEffect(() => {
    const activeJobs = Object.values(jobs).filter(
      j => j.status === 'pending' || j.status === 'processing'
    )
    if (activeJobs.length === 0) return

    const poll = async () => {
      for (const job of activeJobs) {
        try {
          const res = await fetch(
            `/api/projects/${job.projectId}/video-jobs/${job.jobId}`,
            { credentials: 'include' }
          )
          if (!res.ok) continue
          const data = await res.json()
          if (!data.success) continue

          if (data.data.status === 'completed') {
            updateJob(job.jobId, { status: 'completed', videoApiPath: data.data.result?.videoApiPath })
            addToast({
              id: `toast_${Date.now()}_${job.jobId}`,
              jobId: job.jobId,
              projectId: job.projectId,
              status: 'completed',
              message: '動画が完成しました！',
              createdAt: Date.now(),
            })
            // 対象プロジェクトを表示中なら素材データを最新化
            const { useProjectStore } = await import('../stores/projectStore')
            const { getProject } = await import('../lib/api')
            const store = useProjectStore.getState()
            if (store.projectId === job.projectId) {
              const response = await getProject(job.projectId)
              if (response.success && response.data) {
                store.setProject(response.data)
              }
            }
            removeJob(job.jobId)
          } else if (data.data.status === 'failed') {
            updateJob(job.jobId, { status: 'failed', error: data.data.error })
            addToast({
              id: `toast_${Date.now()}_${job.jobId}`,
              jobId: job.jobId,
              projectId: job.projectId,
              status: 'failed',
              message: '動画生成に失敗しました',
              createdAt: Date.now(),
            })
            removeJob(job.jobId)
          }
        } catch {
          // ポーリングエラーは無視して次回リトライ
        }
      }
    }

    const interval = setInterval(poll, 5000)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(Object.keys(jobs))])

  // Remotion レンダージョブのバックグラウンドポーリング（RenderPage が離脱中のみ）
  // RenderPage マウント中は RenderPage 側の独自ポーリングに任せる（自動DLも RenderPage 側で実行）
  useEffect(() => {
    const entries = Object.values(activeRenderJobs)
    if (entries.length === 0 || isRenderPageMounted) return

    const poll = async () => {
      for (const entry of entries) {
        try {
          const result = await getRenderJobStatus(entry.projectId, entry.jobId)
          if (!result.success || !result.data) continue

          if (result.data.status === 'queued') {
            updateRenderJobEntry(entry.jobId, {
              status: 'queued',
              queuePosition: result.data.queuePosition ?? null,
            })
          } else if (result.data.status === 'pending' || result.data.status === 'processing') {
            updateRenderJobEntry(entry.jobId, {
              status: 'processing',
              progress: result.data.progress ?? 0,
              queuePosition: null,
            })
          } else if (result.data.status === 'completed') {
            trackOnce('render:' + entry.jobId, 'generate', { content_type: 'video' })
            // downloadUrl 取得 + project store の renderJobs を最新化
            let downloadUrl: string | undefined
            try {
              const videosResult = await getRenderedVideos(entry.projectId)
              if (videosResult.success && videosResult.data) {
                const job = videosResult.data.find(j => j.id === entry.jobId)
                downloadUrl = job?.downloadUrl ?? undefined
                // 同じプロジェクトを閲覧中なら project store を更新（MaterialsPage 履歴自動更新）
                const currentProj = useProjectStore.getState().project
                if (currentProj?.id === entry.projectId) {
                  useProjectStore.getState().setProject({
                    ...currentProj,
                    renderJobs: videosResult.data,
                  })
                }
              }
            } catch { /* ignore */ }

            // 自動ダウンロード（一度のみ）
            const autoDownload = appSettings?.video?.autoDownloadOnRenderComplete ?? true
            if (autoDownload && downloadUrl && !entry.autoDownloaded) {
              const link = document.createElement('a')
              link.href = downloadUrl
              link.download = ''
              link.click()
              updateRenderJobEntry(entry.jobId, { autoDownloaded: true })
            }

            // 完了トーストを追加
            addRenderToast({
              id: `render_toast_${Date.now()}_${entry.jobId}`,
              jobId: entry.jobId,
              projectId: entry.projectId,
              projectName: entry.projectName,
              status: 'completed',
              createdAt: Date.now(),
              downloadUrl: downloadUrl,
            })
            removeRenderJobEntry(entry.jobId)
          } else if (result.data.status === 'failed') {
            addRenderToast({
              id: `render_toast_${Date.now()}_${entry.jobId}`,
              jobId: entry.jobId,
              projectId: entry.projectId,
              projectName: entry.projectName,
              status: 'failed',
              createdAt: Date.now(),
            })
            removeRenderJobEntry(entry.jobId)
          }
        } catch {
          // ポーリングエラーは無視して次回リトライ
        }
      }
    }

    // 初回即時ポーリング + 1.5s 間隔
    poll()
    const interval = setInterval(poll, 1500)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(Object.keys(activeRenderJobs)), isRenderPageMounted, appSettings?.video?.autoDownloadOnRenderComplete])

  // Keep session cost in sync while working on project/new pages
  useEffect(() => {
    const shouldShowCost =
      location.pathname === '/new' || location.pathname.startsWith('/project/')
    if (!shouldShowCost) return

    let isCancelled = false
    let lastSummaryJson = ''
    const fetchUsageSummary = async () => {
      try {
        const result = await getUsageSummary(projectId)
        if (!isCancelled && result.success && result.data) {
          const json = JSON.stringify(result.data)
          if (json !== lastSummaryJson) {
            lastSummaryJson = json
            setSessionCost(result.data)
          }
        }
      } catch (err) {
        console.error('Failed to fetch usage summary:', err)
      }
    }

    fetchUsageSummary()
    const intervalId = setInterval(fetchUsageSummary, 10000)

    return () => {
      isCancelled = true
      clearInterval(intervalId)
    }
  }, [location.pathname, setSessionCost])

  useEffect(() => {
    const shouldShowCost =
      location.pathname === '/new' || location.pathname.startsWith('/project/')
    if (!shouldShowCost || !isCostDetailOpen) return

    let isCancelled = false
    let isFirst = true
    let lastDetailsJson = ''
    const fetchUsageDetails = async () => {
      if (isFirst) {
        setIsCostDetailsLoading(true)
        setCostDetailsError(null)
      }
      try {
        const result = await getUsageHistory(projectId)
        if (!isCancelled && result.success && result.data) {
          const json = JSON.stringify(result.data)
          if (json !== lastDetailsJson) {
            lastDetailsJson = json
            setCostDetails(result.data)
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setCostDetailsError('明細の取得に失敗しました')
        }
      } finally {
        if (!isCancelled && isFirst) {
          setIsCostDetailsLoading(false)
          isFirst = false
        }
      }
    }

    fetchUsageDetails()
    const intervalId = setInterval(fetchUsageDetails, 10000)
    return () => {
      isCancelled = true
      clearInterval(intervalId)
    }
  }, [isCostDetailOpen, location.pathname, projectId])

  // Reset pagination when modal opens or new data arrives
  useEffect(() => {
    setCostDetailPage(0)
  }, [isCostDetailOpen, costDetails.length])

  // Handle project menu outside click + Escape key
  useEffect(() => {
    if (!isProjectMenuOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!projectMenuRef.current) return
      const target = event.target as Node
      if (!projectMenuRef.current.contains(target)) {
        setIsProjectMenuOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsProjectMenuOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isProjectMenuOpen])

  // Handle cost detail outside click
  useEffect(() => {
    if (!isCostDetailOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!costDetailRef.current) return
      const target = event.target as Node
      if (!costDetailRef.current.contains(target)) {
        setIsCostDetailOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCostDetailOpen])

  const navigateToProjectPath = (path: 'upload' | 'materials' | 'template' | 'storyboard' | 'render') => {
    if (!projectId) return
    setIsProjectMenuOpen(false)
    navigate(`/project/${projectId}/${path}`)
  }

  // Get current page title based on route
  const getPageTitle = () => {
    if (location.pathname === '/' || location.pathname === '/projects') {
      return 'プロジェクト'
    }
    if (location.pathname === '/new') {
      return '新規プロジェクト'
    }
    if (location.pathname.startsWith('/project/')) {
      return 'プロジェクト詳細'
    }
    if (location.pathname === '/media') {
      return 'メディア'
    }
    if (location.pathname === '/costs') {
      return 'コスト分析'
    }
    if (location.pathname === '/settings') {
      return '設定'
    }
    if (location.pathname === '/history') {
      return '操作履歴'
    }
    if (location.pathname === '/account') {
      return 'アカウント'
    }
    if (location.pathname === '/terms-of-use') {
      return '利用規約'
    }
    if (location.pathname === '/manual') {
      return '使い方'
    }
    if (location.pathname === '/transactions') {
      return '取引明細'
    }
    return ''
  }

  // Get icon for current page
  const getPageIcon = () => {
    if (location.pathname === '/' || location.pathname === '/projects' || location.pathname === '/new' || location.pathname.startsWith('/project/')) {
      return <FolderOpen className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/media') {
      return <Image className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/costs') {
      return <BarChart3 className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/settings') {
      return <Settings className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/history') {
      return <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/account') {
      return <UserCircle className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/manual') {
      return <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    if (location.pathname === '/transactions') {
      return <Receipt className="h-5 w-5 text-primary" aria-hidden="true" />
    }
    return null
  }

  // ページネーション用の計算（モーダルJSX内で使う変数をここで定義）
  const costTotalPages = Math.ceil(costDetails.length / COST_PAGE_SIZE)
  const costPagedDetails = costDetails.slice(
    costDetailPage * COST_PAGE_SIZE,
    (costDetailPage + 1) * COST_PAGE_SIZE
  )
  const costCategoryLabel: Record<string, string> = {
    generation: '画像生成',
    text: 'テキスト生成',
    analysis: '画像評価',
    tts: '音声生成',
    video: 'AI動画',
  }
  const costCategoryColor: Record<string, string> = {
    generation: 'text-primary bg-primary-subtle',
    text: 'text-primary bg-primary-subtle',
    analysis: 'text-warn bg-warn-bg',
    tts: 'text-ok bg-ok-bg',
    video: 'text-primary bg-primary-subtle',
  }

  return (
    <div className="gm-app gm-shell min-h-screen">
      {/* 全アプリ共通トップバー「パール」（72px・残高を常に表示） */}
      <TopBar onOpenMenu={() => setMobileSidebarOpen(true)} menuOpen={mobileSidebarOpen} />

      {/* Sidebar（浮かせた白い板・248px / 畳み時 64px） */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Main Content（ドロワーが開いている間は inert にして、Tab・クリックが背後へ届かないようにする） */}
      <div
        inert={mobileSidebarOpen ? true : undefined}
        className={clsx(
          'flex min-h-screen flex-col pt-[var(--gm-shell-topbar-h)] transition-[margin] duration-200 motion-reduce:transition-none',
          // 浮かせた板（幅 + 左右の gap）の分だけ空ける
          sidebarCollapsed ? 'md:ml-[calc(4rem+var(--gm-shell-gap)*2)]' : 'md:ml-[calc(var(--gm-shell-sidebar-w)+var(--gm-shell-gap)*2)]'
        )}
      >
        {/* ページヘッダー（画面タイトルと、この画面固有の操作。グローバルなものはトップバーに集約済み） */}
        <header className="sticky top-[var(--gm-shell-topbar-h)] z-30 h-14" style={{ background: 'var(--gm-shell-bg)', backgroundAttachment: 'fixed' }}>
          <div className="flex h-full items-center justify-between gap-2 px-4 md:px-6">
            {/* Left: Page Title */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h1 className="flex min-w-0 items-center gap-2 truncate text-base font-bold text-ink md:text-lg">
                {getPageIcon()}
                <span className="truncate">{getPageTitle()}</span>
              </h1>
            </div>

            {/* Right side - Quick nav + project menu */}
            {(location.pathname === '/new' || location.pathname.startsWith('/project/')) && (
              <div className="flex items-center gap-2">
                {/* Quick nav buttons (project sub-pages only) */}
                {location.pathname.startsWith('/project/') && projectId && (() => {
                  const currentSub = location.pathname.split('/').pop()
                  const quickNav = [
                    { path: 'materials' as const, icon: <Package className="h-4 w-4" aria-hidden="true" />, label: '素材の編集' },
                    { path: 'template' as const, icon: <Film className="h-4 w-4" aria-hidden="true" />, label: '動画を生成' },
                  ]
                  return quickNav.map(item => (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => navigateToProjectPath(item.path)}
                      title={item.label}
                      aria-current={currentSub === item.path ? 'page' : undefined}
                      className={clsx(
                        'gm-btn',
                        currentSub === item.path ? 'bg-primary-subtle text-primary border-primary' : 'gm-btn-secondary'
                      )}
                    >
                      {item.icon}
                      <span className="hidden sm:inline">{item.label}</span>
                    </button>
                  ))
                })()}

                {/* Project menu (storyboard / session cost) */}
                <div ref={projectMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setIsProjectMenuOpen((prev) => !prev)}
                    className={clsx(
                      'gm-btn gm-btn-ghost w-control px-0',
                      isProjectMenuOpen && 'bg-surface-2 text-ink'
                    )}
                    aria-label="プロジェクトメニューを開く"
                    aria-expanded={isProjectMenuOpen}
                    aria-haspopup="true"
                  >
                    {isProjectMenuOpen
                      ? <X className="h-5 w-5" aria-hidden="true" />
                      : <Menu className="h-5 w-5" aria-hidden="true" />
                    }
                  </button>

                  {/* Dropdown Panel - storyboard + cost */}
                  {isProjectMenuOpen && location.pathname.startsWith('/project/') && projectId && (() => {
                    const currentSub = location.pathname.split('/').pop()
                    return (
                      <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-card border border-line bg-surface shadow-overlay">
                        <div className="p-1.5">
                          {/* Storyboard - SBモードのみ表示 */}
                          {currentProject?.storyboardMode && (
                            <button
                              type="button"
                              onClick={() => { navigateToProjectPath('storyboard'); setIsProjectMenuOpen(false) }}
                              className={clsx(
                                'flex min-h-row w-full items-center gap-3 rounded-ctl px-3 py-2 text-left transition-colors',
                                currentSub === 'storyboard' ? 'bg-surface-2' : 'hover:bg-surface-2'
                              )}
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ctl bg-primary-subtle text-primary">
                                <Film className="h-4 w-4" aria-hidden="true" />
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold leading-tight text-ink">台本モード・絵コンテ</div>
                                <div className="mt-0.5 text-xs leading-tight text-ink-muted">シーン編集・動画クリップ再生成</div>
                              </div>
                            </button>
                          )}
                        </div>
                        {/* Cost */}
                        <div className="border-t border-line p-1.5">
                          <button
                            type="button"
                            onClick={() => { setIsCostDetailOpen(true); setIsProjectMenuOpen(false) }}
                            className="flex min-h-row w-full items-center gap-3 rounded-ctl px-3 py-2 text-left transition-colors hover:bg-surface-2"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ctl bg-primary-subtle text-primary">
                              <Receipt className="h-4 w-4" aria-hidden="true" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-bold leading-tight text-ink">プロジェクトコスト</div>
                              <div className="mt-0.5 text-xs leading-tight text-ink-muted">動画素材生成コストの詳細</div>
                            </div>
                            <span className="gm-num shrink-0 text-sm font-bold text-ink">
                              {totals.total.jpy.toFixed(2)} cr
                            </span>
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>

        {/* 保存バーが画面下に固定されている画面は、バーの高さ(約 64px)だけフッターの下に余白を足す */}
        <GenemoFooter bottomInset={location.pathname.startsWith('/admin/decor-parts') ? 64 : 0} />
      </div>

      {/* Cost Detail Modal — ヘッダー外に配置（backdrop-filterの影響を回避） */}
      {isCostDetailOpen && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center"
          style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(4px)' }}
          onClick={() => setIsCostDetailOpen(false)}
        >
          <div
            ref={costDetailRef}
            className="w-[860px] bg-bg border border-line rounded-2xl shadow-2xl flex flex-col"
            style={{ maxHeight: '82vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-line shrink-0">
              <div>
                <p className="text-base font-semibold text-white flex items-center gap-2"><Receipt className="w-5 h-5 text-ok" />プロジェクトコスト明細</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  合計: <span className="text-primary font-medium">{totals.total.jpy.toFixed(2)} cr</span>
                </p>
              </div>
              <button
                onClick={() => setIsCostDetailOpen(false)}
                className="p-1.5 rounded-lg text-ink-muted hover:text-white hover:bg-surface transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Category Summary Grid */}
            <div className="grid grid-cols-5 gap-3 px-6 py-4 border-b border-line shrink-0">
              {[
                {
                  label: '画像生成',
                  key: 'generation' as const,
                  icon: <ImagePlus className="w-4 h-4" />,
                  color: 'text-primary',
                  bg: 'bg-primary-subtle border-primary',
                },
                {
                  label: 'テキスト生成',
                  key: 'text' as const,
                  icon: <FileText className="w-4 h-4" />,
                  color: 'text-primary',
                  bg: 'bg-primary-subtle border-primary',
                },
                {
                  label: '画像評価',
                  key: 'analysis' as const,
                  icon: <ScanSearch className="w-4 h-4" />,
                  color: 'text-warn',
                  bg: 'bg-warn-bg border-warn',
                },
                {
                  label: '音声生成',
                  key: 'tts' as const,
                  icon: <Mic2 className="w-4 h-4" />,
                  color: 'text-ok',
                  bg: 'bg-ok-bg border-ok',
                },
                {
                  label: 'AI動画',
                  key: 'video' as const,
                  icon: <Film className="w-4 h-4" />,
                  color: 'text-primary',
                  bg: 'bg-primary-subtle border-primary',
                },
              ].map((cat) => (
                <div key={cat.key} className={`rounded-xl border p-3 ${cat.bg}`}>
                  <div className={`flex items-center gap-1.5 ${cat.color} mb-2`}>
                    {cat.icon}
                    <span className="text-xs font-medium">{cat.label}</span>
                  </div>
                  <p className="text-lg font-bold text-white leading-none">
                    {totals[cat.key].cost.jpy.toFixed(2)} cr
                  </p>
                  <p className="text-xs text-ink-muted mt-1">
                    {totals[cat.key].apiCalls}回
                    {totals[cat.key].imageCount > 0 && ` / ${totals[cat.key].imageCount}枚`}
                  </p>
                </div>
              ))}
            </div>

            {/* Detail List */}
            <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4">
              {isCostDetailsLoading && (
                <div className="text-xs text-ink-muted py-6 text-center">明細を読み込み中...</div>
              )}
              {costDetailsError && (
                <div className="text-xs text-bad py-6 text-center">{costDetailsError}</div>
              )}
              {!isCostDetailsLoading && !costDetailsError && costDetails.length === 0 && (
                <div className="text-xs text-ink-muted py-6 text-center">明細データはありません</div>
              )}
              {!isCostDetailsLoading && !costDetailsError && costPagedDetails.length > 0 && (
                <div className="space-y-1.5">
                  {costPagedDetails.map((row, index) => (
                    <div
                      key={`${row.timestamp}-${row.model}-${costDetailPage}-${index}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 bg-surface-2 hover:bg-surface-2 transition-colors"
                    >
                      <span
                        className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${costCategoryColor[row.category] ?? 'text-ink-muted bg-surface-2'}`}
                      >
                        {costCategoryLabel[row.category] ?? row.category}
                      </span>
                      <div className="flex-1 min-w-0">
                        {row.step && <div className="text-[10px] text-ink-muted leading-tight truncate">{row.step}</div>}
                        <div className="text-xs text-ink truncate">{row.model}</div>
                      </div>
                      <span className="text-xs text-ink-muted shrink-0">
                        {row.imageCount ? `${row.imageCount}枚` : `${row.tokens.total.toLocaleString()} tok`}
                      </span>
                      <span className="text-xs font-medium text-primary shrink-0 w-16 text-right">
                        {row.cost.jpy.toFixed(2)} cr
                      </span>
                      <span className="text-[10px] text-ink-muted shrink-0 w-10 text-right">
                        {new Date(row.timestamp).toLocaleTimeString('ja-JP', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {costTotalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-line shrink-0">
                <span className="text-xs text-ink-muted">
                  {costDetailPage * COST_PAGE_SIZE + 1}–{Math.min((costDetailPage + 1) * COST_PAGE_SIZE, costDetails.length)} / {costDetails.length}件
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCostDetailPage((p) => Math.max(0, p - 1))}
                    disabled={costDetailPage === 0}
                    className="p-1 rounded-md text-ink-muted hover:text-white hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: costTotalPages }, (_, i) => i).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCostDetailPage(page)}
                      className={clsx(
                        'min-w-[28px] h-7 rounded-md text-xs font-medium transition-all',
                        page === costDetailPage
                          ? 'bg-primary text-white'
                          : 'text-ink-muted hover:text-white hover:bg-surface'
                      )}
                    >
                      {page + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCostDetailPage((p) => Math.min(costTotalPages - 1, p + 1))}
                    disabled={costDetailPage === costTotalPages - 1}
                    className="p-1 rounded-md text-ink-muted hover:text-white hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 動画生成ジョブ通知（右上固定トースト） */}
      <VideoJobToast />

      {/* 分析完了通知（バックグラウンドポーラー兼トースト） */}
      <AnalysisCompleteToast />

      {/* OAuthログイン成功通知 */}
      <OAuthLoginToast />

      {/* チャットボットウィジェット（公開設定で有効かつユーザーが非表示設定していない時のみ表示） */}
      {appSettings?.ui?.showChatbot === true && <ChatWidget />}
    </div>
  )
}

export default AppLayout
